import {
  Badge,
  Button,
  ManageableItemsList,
  TTableRowAction,
} from '@payaca/ambrosia';
import { FC, useCallback } from 'react';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  MaterialsListMaterial,
  MaterialsListSummaryItems,
} from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import { humanizeCurrency } from '@/helpers/currency';
import { useUserHasPermission } from '@/hooks/usePermissions';

import {
  getAccountRegionFromCurrencyCodeLegacy,
  getRegionalTextString,
} from '@payaca/helpers/internationalHelper';
import { roundToDecimalPlaces } from '@payaca/helpers/numberHelper';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { AccountRegions } from '@payaca/types/accountTypes';
import { Deal } from '@payaca/types/dealTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { Link } from 'react-router-dom';
import { GetMaterialsListOrderSummaryItemsSortBy } from '../../../../../gql/graphql';
import { useSummaryItemsInputPaginationState } from './materialsPanelHooks';
import { SupplierBadges } from './SupplierBadges';

export const OrderedPanel: FC<{
  projectId?: Deal['id'];
  summaryItems: MaterialsListSummaryItems | undefined;
  inputPaginationState: Omit<
    ReturnType<typeof useSummaryItemsInputPaginationState>,
    'setStatuses'
  >;
  onRequestDeleteMaterialPurchaseRecord: (row: MaterialsListMaterial) => void;
  onMaterialView: (materialId: string) => void;
}> = (props) => {
  const {
    summaryItems,
    projectId,
    inputPaginationState: {
      pagination,
      input,
      setCurrentPage,
      setSearchTerm,
      setSort,
    },
    onRequestDeleteMaterialPurchaseRecord,
    onMaterialView,
  } = props;

  const { data: regionPreferences } = useGetMyRegionPreferences();

  const region = regionPreferences
    ? getAccountRegionFromCurrencyCodeLegacy(regionPreferences?.currency.code)
    : AccountRegions.UK;

  const hasDeleteMprPermission = useUserHasPermission({
    permissions: [MaterialsListPermissions.DELETE_MATERIAL_PURCHASE_RECORD],
  });

  const getItemActions = useCallback(
    (row: MaterialsListMaterial) => {
      const actions: TTableRowAction<MaterialsListMaterial>[] = [];

      if (
        hasDeleteMprPermission &&
        row.__typename === 'MaterialsListOrderSummaryPurchasedItem'
      ) {
        actions.push({
          label: 'Delete Purchase Record',
          onClick: onRequestDeleteMaterialPurchaseRecord,
        });
      }

      return actions;
    },
    [hasDeleteMprPermission, onRequestDeleteMaterialPurchaseRecord]
  );

  const onSort = (field?: string, direction?: SortDirection) => {
    setSort(
      field as GetMaterialsListOrderSummaryItemsSortBy,
      direction === SortDirection.ASCENDING ? 'ASC' : 'DESC'
    );
  };

  return (
    <div className="@container">
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput onChange={setSearchTerm} />
      </ManageableItemsList.ActionBar>
      <ManageableItemsList.Table<MaterialsListMaterial>
        isLoading={!summaryItems}
        items={summaryItems?.items || []}
        itemActions={getItemActions}
        uniqueKey="id"
        sortBy={(input.sortBy as keyof MaterialsListMaterial) || undefined}
        sortDirection={
          input.sortDirection === 'ASC'
            ? SortDirection.ASCENDING
            : SortDirection.DESCENDING
        }
      >
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Material"
          field="id"
          render={(id, row) => (
            <div className="max-w-[400px] truncate">
              <Button
                variant="link-inline"
                onClick={() =>
                  onMaterialView(row.materialsListMaterial.material.id)
                }
              >
                {row.materialsListMaterial.material.name}
              </Button>
            </div>
          )}
          onSort={onSort}
          sortByName="MATERIAL_NAME"
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Qty"
          field="id"
          render={(id, row) => roundToDecimalPlaces(row.materialQuantity, 2)}
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="PO"
          field="id"
          render={(id, row) => {
            if (
              row.__typename === 'MaterialsListOrderSummaryPurchasedItem' &&
              row.materialPurchaseRecord?.purchaseOrder
            )
              return (
                <Link
                  to={`/purchaseOrders/${row.materialPurchaseRecord?.purchaseOrder?.id}?projectId=${projectId}`}
                >
                  {row.materialPurchaseRecord?.purchaseOrder?.reference}
                </Link>
              );
            if (
              row.__typename === 'MaterialsListOrderSummaryOrderedItem' &&
              row.materialPurchaseIntent?.purchaseOrder
            )
              return (
                <Link
                  to={`/purchaseOrders/${row.materialPurchaseIntent?.purchaseOrder?.id}?projectId=${projectId}`}
                >
                  {row.materialPurchaseIntent?.purchaseOrder?.reference}
                </Link>
              );
          }}
        />

        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Supplier"
          field="id"
          render={(id, row) => {
            let supplier = null;
            if (row.__typename === 'MaterialsListOrderSummaryPurchasedItem')
              supplier = row.materialPurchaseRecord?.supplier;
            if (row.__typename === 'MaterialsListOrderSummaryOrderedItem')
              supplier = row.materialPurchaseIntent?.supplier;

            return supplier && <SupplierBadges suppliers={[supplier]} />;
          }}
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          className="text-right"
          header={`Total (ex ${getRegionalTextString(
            region,
            RegionalStrings.VALUE_ADDED_TAX
          )})`}
          field="id"
          render={(id, row) => {
            let price = null;
            if (row.__typename === 'MaterialsListOrderSummaryPurchasedItem')
              price = row.materialPurchaseRecord?.price.totalPriceExcTax;
            if (row.__typename === 'MaterialsListOrderSummaryOrderedItem')
              price =
                row.materialPurchaseIntent?.predictedPrice.totalPriceExcTax;

            return price && humanizeCurrency(price);
          }}
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Status"
          field="id"
          render={(id, row) => {
            if (row.status === 'PURCHASED') {
              return (
                <Badge variant="soft" colour="green">
                  Purchased
                </Badge>
              );
            }

            return (
              <Badge variant="soft" colour="yellow">
                Ordered
              </Badge>
            );
          }}
        />
      </ManageableItemsList.Table>
      {summaryItems && (
        <ManageableItemsList.PaginationBar
          type="custom"
          totalItems={summaryItems.totalCount}
          pageSize={summaryItems.limit}
          currentPage={summaryItems.offset / summaryItems.limit + 1}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};
